import {customElement, property, query, state} from 'lit/decorators.js';
import {css, html, LitElement, PropertyValues} from 'lit';
import {translate, TranslateService} from '@stefanholzapfel/lit-translate';
import {repeat} from 'lit-html/directives/repeat.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import {unsafeCSSIcon} from '../../../styles/icons';

import type {ComposeOption, ECharts} from 'echarts/core';
import * as echarts from 'echarts/core';
import {BarChart, BarSeriesOption} from 'echarts/charts';
import {
    DatasetComponent,
    GridComponent,
    GridComponentOption,
    TooltipComponent,
    TooltipComponentOption,
    TransformComponent,
} from 'echarts/components';
import {LabelLayout, UniversalTransition} from 'echarts/features';
import {CanvasRenderer} from 'echarts/renderers';
import {SpStatisticField} from '../sp-statistic-control/sp-statistic-control';
import {when} from 'lit/directives/when.js';

type EChartsOption = ComposeOption<
    | BarSeriesOption
    | TooltipComponentOption
    | GridComponentOption
>;

echarts.use([
    BarChart,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
    LabelLayout,
    UniversalTransition,
    CanvasRenderer
]);

@customElement('sp-statistic-slide-field-ranking')
export class SpStatisticSlideFieldRanking extends LitElement {

    _chartInstance: ECharts | undefined;
    _chartElemObserver: ResizeObserver | undefined;

    @property({type: String})
    intro: String;
    @property({type: Array})
    data: SpStatisticFieldRankingEntry[] = [];
    @property({type: Array})
    field: SpStatisticField
    @property({type: Boolean})
    loading: Boolean;

    @state()
    private _dataFieldId: number

    @query("#field-ranking-chart", true)
    private _chartElem: HTMLDivElement | undefined | null

    colors: string[] = ['#7499a0', '#c16667', '#b6976a', '#a07482', '#929f7a', '#d5ce91', '#9494ba', '#89bf9f', '#cf8888', '#97c0cd'];

    static styles = [
        css`
            :host {
                padding-bottom: 30px;
            }

            .description {
                margin: 15px;
                line-height: 18px;
            }

            .field {
                margin: 15px 15px 0 15px;
                line-height: 18px;
                font-weight: bold;
            }

            #field-ranking-chart {
                width: 100%;
                height: 250px;
            }

            .list {
                width: 100%;
                padding: 0 15px;
                margin: 0;
                font-size: 14px;
                line-height: 18px;
                box-sizing: border-box;
            }

            .list td.pos {
                width: 25px;
            }

            .list td.total {
                width: 50px;
                padding-left: 20px;
                background-image: url(${unsafeCSSIcon('spotteron', { size: 'x128', color: 'grey-dark' })});
                background-position: left center;
                background-repeat: no-repeat;
                background-size: 14px auto;
            }

            .list td.color {
                width: 20px;
            }

            .list td.color div {
                width: 12px;
                height: 12px;
            }

            .list td.name {
                padding-right: 10px;
            }

            .spot-statistics .contents .list .hidden td.name {
                text-decoration: line-through;
            }

            .no-data {
                padding: 15px;
            }
        `
    ];

    private updateEcharts() {
        const xAxisData = this.data != undefined ? this.data.map((ele, index) => index + 1) : [];
        const seriesData = this.data != undefined ? this.data.map((ele, index) => {
            return {
                value: ele.count,
                itemStyle: {
                    color: this.colors[index]
                }
            }
        }) : [];
        this._chartInstance?.setOption({
            grid: {
                top: '30px',
                bottom: '30px',
                left: '15px',
                right: '15px',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: xAxisData
            },
            yAxis: {},
            series: [
                {
                    type: 'bar',
                    data: seriesData,
                }
            ]
        });
    }

    private onChartElemResize = () => {
        this._chartInstance.resize();
    }

    protected firstUpdated(): void {
        if (this._chartElem !== null && this._chartElem !== undefined) {
            this._chartInstance = echarts.init(this._chartElem);
            this._chartElemObserver = new ResizeObserver(this.onChartElemResize);
            this._chartElemObserver.observe(this._chartElem);
        }
    }

    protected updated(changedProperties: PropertyValues<this>): void {
        if (changedProperties.has('data')) {
            this._dataFieldId = this.field.id;
            this.updateEcharts();
        }
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this._chartInstance?.dispose();
        this._chartElemObserver.disconnect();
    }

    render() {
        return html`
            <div class="description">${ this.intro }</div>
            <div class="field">${ translate(`CONFIG_FIELD_${this.field.id}_LABEL`) }:</div>
            ${ when(!this.loading && (!this.data || this.data.length == 0), () => html`
                <div class="no-data">
                    ${ translate('STATISTICS_NO_DATA') }
                </div>
            `)}
            <div id="field-ranking-chart" style=${ !(this.data && this.data.length > 0) ? 'visibility:hidden;' : '' }></div>
            ${ when(this.data && this.data.length > 0, () => html`
                <table class="list">
                    ${repeat(this.data ? this.data : [], (entry) => entry.value, (entry, index) => html`
                        <tr>
                            <td class="pos">${entry.pos}.</td>
                            <td class="color">
                                <div style="background-color: ${this.colors[index]};"></div>
                            </td>
                            <td class="name">
                                ${ when(this.field.options && this.field.options.length > 0, () => html`
                                    ${unsafeHTML(TranslateService.translate(`CONFIG_FIELD_${this._dataFieldId}_OPTION_${entry.value}`.toUpperCase()) as string)}
                                `, () => `${entry.value}`)}
                            </td>
                            <td class="total">${entry.count}</td>
                        </tr>
                    `)}
                </table>
            `)}
        `;
    }
}

export interface SpStatisticFieldRankingEntry {
    pos: number;
    value: number;
    count: number;
}